/**
 * Реализация раздела Группы трафика для числовых идентификаторов
 */

import React, { useEffect, useState } from "react";
import { Collapse, Form, message, Row, Tooltip } from "antd";
import { FormInstance } from "antd/es/form/Form";
import { Moment } from "moment";
import { combineClasses } from "../../../../lib/utils/combineClasses";
import { ReactComponent as PlusIcon } from "../../../../img/plus.svg";
import {
  IOfferListSelectMode,
  IOfferAggRuleFormData,
} from "../../../../containers/OfferContainer/context/types";
import { AggregationRule } from "../../../../lib/types/AggregationRule";
import { rangeFromLimitValidator } from "../../../../components/common/ServiceRate/utils/rangeFromLimitValidator";
import { rangeToLimitValidator } from "../../../../components/common/ServiceRate/utils/rangeToLimitValidator";
import { IRuleCondition } from "../../../../lib/types/IRuleCondition";
import { useAppSelector } from "../../../..";
import { AggregationRuleEditItem } from "./AggregationRuleEditItem/AggregationRuleEditItem";
import useDebounce from "../../../../lib/hooks/useDebounce";
import "./AggregationRulesEdit.less";

type FieldName =
  | "aggName"
  | "periodTypeId"
  | "rangeFrom"
  | "rangeTo"
  | "ruleCondition"
  | "fromDate"
  | "toDate";

interface ComponentProps {
  className?: string;
  aggregationRulesForm: FormInstance<IOfferAggRuleFormData>;
  getNewAggregationRule: () => AggregationRule<"createForm">;
  requiredFields?: FieldName[];
  availableFields: FieldName[];
  newRuleCondition: IRuleCondition;
  setAggregationRuleList?: (aggregationRules: AggregationRule<"createForm">[]) => void;
  hideCollapse?: true; // Скрытие сворачивания и кнопки добавления
  availableDates?: { fromDate: Moment | undefined; toDate: Moment | undefined };
  hidePeriod?: true; // Скрытие столбцов с датами
  offerListSelectMode?: IOfferListSelectMode;
  updateCollapse?: boolean;
}

export const AggregationRulesEdit = (props: ComponentProps) => {
  const {
    className,
    aggregationRulesForm,
    getNewAggregationRule,
    availableFields,
    requiredFields = [],
    newRuleCondition,
    setAggregationRuleList,
    hideCollapse,
    availableDates,
    hidePeriod,
    offerListSelectMode,
    updateCollapse,
  } = props;

  const {
    retailNetworks: { retailNetworksUniqueIdDic },
    serviceUsageDocumentType,
    periodTypes,
  } = useAppSelector((state) => state.rtkDictionaries);

  const addAggregationRule = () => {
    const { aggregationRules } = aggregationRulesForm.getFieldsValue();
    if (aggregationRules) {
      aggregationRulesForm.setFieldsValue({
        aggregationRules: [...aggregationRules, getNewAggregationRule()],
      });
    } else {
      aggregationRulesForm.setFieldsValue({
        aggregationRules: [getNewAggregationRule()],
      });
    }
  };

  const copyAggregationRule = (idx: number) => {
    const { aggregationRules } = aggregationRulesForm.getFieldsValue();
    if (aggregationRules) {
      const copiedRule = { ...aggregationRules[idx], id: Date.now() };
      aggregationRulesForm.setFieldsValue({
        aggregationRules: [
          ...aggregationRules.slice(0, idx),
          copiedRule,
          ...aggregationRules.slice(idx),
        ],
      });
    }
  };

  const getRangeFrom = (idx: number) =>
    aggregationRulesForm.getFieldValue(["aggregationRules", idx, "rangeFrom"]);
  const getRangeTo = (idx: number) =>
    aggregationRulesForm.getFieldValue(["aggregationRules", idx, "rangeTo"]);

  const rangeFromValidator = (rangeFrom: number, idx: number): Promise<void> => {
    const isValueValid = rangeFromLimitValidator({
      rangeFrom,
      limitTo: getRangeTo(idx),
      isRequired: requiredFields.includes("rangeFrom"),
    });
    aggregationRulesForm.setFields([
      {
        name: ["aggregationRules", idx, "rangeTo"],
        errors: isValueValid ? [] : [""],
      },
    ]);
    return isValueValid ? Promise.resolve() : Promise.reject();
  };

  const rangeFromOnBlur = (rangeFrom: number, idx: number): void => {
    const isValueValid = rangeFromLimitValidator({
      rangeFrom,
      limitTo: getRangeTo(idx),
    });
    if (!isValueValid) {
      message.warning('Значение "От" не должно быть больше значения "До"');
    }
  };

  const rangeToValidator = (rangeTo: number, idx: number): Promise<void> => {
    const isValueValid = rangeToLimitValidator({
      limitFrom: getRangeFrom(idx),
      rangeTo,
      isRequired: requiredFields.includes("rangeTo"),
    });
    aggregationRulesForm.setFields([
      {
        name: ["aggregationRules", idx, "rangeFrom"],
        errors: isValueValid ? [] : [""],
      },
    ]);
    return isValueValid ? Promise.resolve() : Promise.reject();
  };

  const rangeToOnBlur = (rangeTo: number, idx: number): void => {
    const isValueValid = rangeToLimitValidator({
      limitFrom: getRangeFrom(idx),
      rangeTo,
    });
    if (!isValueValid) {
      message.warning('Значение "До" не должно быть меньше значения "От"');
    }
  };

  const getIncludeListTitle = (aggRuleIdx: number, idx: number): string => {
    const includeList = aggregationRulesForm.getFieldValue([
      "aggregationRule",
      aggRuleIdx,
      "ruleCondition",
      idx,
      "includeList",
    ]);

    const selectedNetworkNames: any[] = [];

    if (Array.isArray(includeList)) {
      includeList.forEach((recObjectId) => {
        const selectedNetwork = retailNetworksUniqueIdDic.find(
          ({ objectId }) => String(objectId) === String(recObjectId)
        );
        const selectedNetworkName = selectedNetwork
          ? (selectedNetwork as any)?.stringValue
          : "!Сеть неизвестна!";
        if (!selectedNetworkNames.includes(selectedNetworkName)) {
          selectedNetworkNames.push(selectedNetworkName);
        }
      });
    }

    return selectedNetworkNames.join("\n");
  };

  const getDocumentTypeListTitle = (aggRuleIdx: number, idx: number): string => {
    const documentTypeList = aggregationRulesForm.getFieldValue([
      "aggregationRule",
      aggRuleIdx,
      "ruleCondition",
      idx,
      "documentTypeList",
    ]);

    const selectedDocumentNames: string[] = [];

    if (Array.isArray(documentTypeList)) {
      documentTypeList.forEach((recId) => {
        const docType = serviceUsageDocumentType.find(({ id }) => String(id) === String(recId));
        if (docType) {
          selectedDocumentNames.push(docType?.alias);
        }
      });
    }

    return selectedDocumentNames.join("\n");
  };

  const onFormValuesChange = useDebounce((aggregationRules: AggregationRule<"createForm">[]) => {
    if (setAggregationRuleList) {
      setAggregationRuleList(aggregationRules);
    }
  }, 1000);

  const [collapseKeys, setCollapseKeys] = useState<string | string[]>([]);

  // Дефолтное открытие Collapse, если есть группы трафика
  useEffect(() => {
    // Тайм-аут, чтобы исходные значения успели записаться в форму
    setTimeout(() => {
      const aggregationRules = aggregationRulesForm.getFieldValue("aggregationRules");
      if (aggregationRules?.length) {
        setCollapseKeys(["1"]);
      } else {
        setCollapseKeys([]);
      }
    }, 100);
  }, [updateCollapse]);

  const disableFromDate = (date: Moment, idx: number): boolean => {
    const toDate = (() => {
      if (availableFields.includes("toDate")) {
        return (
          aggregationRulesForm.getFieldValue(["aggregationRules", idx, "toDate"]) ||
          availableDates?.toDate
        );
      }
      return availableDates?.toDate;
    })();
    const offerFromDate = availableDates?.fromDate;
    if (offerFromDate && toDate) {
      return !date.isBetween(offerFromDate, toDate, "day", "[]");
    }
    return false;
  };

  const disableToDate = (date: Moment, idx: number): boolean => {
    const fromDate = (() => {
      if (availableFields.includes("fromDate")) {
        return (
          aggregationRulesForm.getFieldValue(["aggregationRules", idx, "fromDate"]) ||
          availableDates?.fromDate
        );
      }
      return availableDates?.fromDate;
    })();
    const offerToDate = availableDates?.toDate;
    if (fromDate && offerToDate) {
      return !date.isBetween(fromDate, offerToDate, "day", "[]");
    }
    return false;
  };

  const renderContent = () => (
    <Form
      form={aggregationRulesForm}
      labelAlign="left"
      onValuesChange={(v, { aggregationRules }) => {
        // Срабатывает в т.ч. при удалении строки, и почему-то передает лишнюю запись с undefined значениями. Фильтр, для устранения этого
        onFormValuesChange(aggregationRules?.filter(({ id }) => !!id));
      }}
    >
      <Form.Item
        className="form-item-required__hidden-error__mb0"
        dependencies={["aggregationRules"]}
      >
        {() => (
          <Form.List name="aggregationRules">
            {(fields, { remove }) => {
              const onRemove = (idx: number) => {
                if (fields.length === 1) {
                  setCollapseKeys([]);
                }
                remove(idx);
              };
              return (
                <>
                  {fields.map(({ key }, idx) => (
                    <AggregationRuleEditItem
                      key={key}
                      availableFields={availableFields}
                      requiredFields={requiredFields}
                      periodTypes={periodTypes}
                      newRuleCondition={newRuleCondition}
                      idx={idx}
                      rangeFromValidator={rangeFromValidator}
                      rangeFromOnBlur={rangeFromOnBlur}
                      rangeToValidator={rangeToValidator}
                      rangeToOnBlur={rangeToOnBlur}
                      getIncludeListTitle={getIncludeListTitle}
                      getDocumentTypeListTitle={getDocumentTypeListTitle}
                      onRemove={onRemove}
                      hideAdd={hideCollapse}
                      disableFromDate={(date) => disableFromDate(date, idx)}
                      disableToDate={(date) => disableToDate(date, idx)}
                      hidePeriod={hidePeriod}
                      copyAggregationRule={copyAggregationRule}
                      offerListSelectMode={offerListSelectMode}
                    />
                  ))}
                </>
              );
            }}
          </Form.List>
        )}
      </Form.Item>
    </Form>
  );

  return (
    <div className={combineClasses("aggregationRules-edit", className)}>
      {!hideCollapse && (
        <Collapse
          bordered={false}
          onChange={(keys) => {
            setCollapseKeys(keys);
          }}
          activeKey={collapseKeys}
          // className={hideCollapse ? "aggregationRules-edit__hideCollapse" : undefined}K
        >
          <Collapse.Panel
            key="1"
            header={
              <Row justify="space-between" className="aggregationRules-edit__pageTitle">
                <h3>Группы трафика</h3>
                {offerListSelectMode !== "select" && (
                  <Tooltip title="Добавить группу трафика">
                    <PlusIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        addAggregationRule();
                        setCollapseKeys(["1"]);
                      }}
                      className="aggregationRules-edit__addButton"
                    />
                  </Tooltip>
                )}
              </Row>
            }
          >
            {renderContent()}
          </Collapse.Panel>
        </Collapse>
      )}
      {hideCollapse && <>{renderContent()}</>}
    </div>
  );
};
